import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, CreditCard, Loader2, MapPin, Navigation, Store } from "lucide-react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";

const StampCardCard = ({ card, isCustomerAuthenticated, isActivated, activationStateLoading, activating, onActivate }) => {
  const [locationsOpen, setLocationsOpen] = useState(false);

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const validityText =
    card.validFrom || card.validUntil
      ? `${card.validFrom ? formatDate(card.validFrom) : "ab sofort"} - ${card.validUntil ? formatDate(card.validUntil) : "offen"}`
      : "Unbegrenzt gültig";

  const locations = Array.isArray(card.locations) ? card.locations : [];
  const uniqueCities = [...new Set(locations.map((location) => location.city).filter(Boolean))];
  const firstCity = uniqueCities[0] || "Mehrere Städte";
  const additionalCities = Math.max(0, uniqueCities.length - 1);

  return (
    <>
      <div
        className="bg-background rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
        onClick={() => setLocationsOpen(true)}
      >
        {card.thumbnailUrl ? (
          <div className="h-48 relative overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={card.thumbnailUrl}
                alt=""
                className="w-full h-full object-cover blur-xl brightness-50 scale-110"
              />
            </div>
            <img
              src={card.thumbnailUrl}
              alt={card.name}
              className="relative w-full h-full object-contain z-10"
            />
          </div>
        ) : (
          <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
            <CreditCard className="w-16 h-16 text-white opacity-50" />
          </div>
        )}

        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            {card.company?.logoUrl ? (
              <img
                src={card.company.logoUrl}
                alt={card.company.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-border"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                {(card.company?.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-semibold text-foreground">{card.company?.name}</p>
              <p className="text-sm text-muted-foreground">Stempelkarte</p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-foreground mb-2">{card.name}</h3>
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="text-lg font-bold">
              {card.stampCount} Stempel · {card.reward}
            </Badge>
          </div>

          {card.description ? <p className="text-foreground mb-4 text-sm">{card.description}</p> : null}

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Calendar className="w-4 h-4" />
            <span>{validityText}</span>
          </div>

          {locations.length > 0 ? (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">
                  {firstCity}
                  {additionalCities > 0 ? ` +${additionalCities} ${additionalCities === 1 ? "Stadt" : "Städte"}` : ""}
                  {` und ${locations.length} Standort${locations.length !== 1 ? "e" : ""}`}
                </span>
              </div>
            </div>
          ) : null}

          <Button className="w-full" variant="default" onClick={() => setLocationsOpen(true)}>
            Standorte ansehen
          </Button>
          <Button
            className="w-full mt-2"
            variant="outline"
            disabled={!!isActivated || !!activating || !!activationStateLoading}
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onActivate === "function") onActivate(card);
            }}
          >
            {activating
              ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Lädt...
                </>
              )
              : activationStateLoading
                ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Lädt...
                  </>
                )
              : isActivated
                ? "Bereits hinzugefügt"
                : isCustomerAuthenticated
                  ? "Stempelkarte hinzufügen"
                  : "Anmelden zum Hinzufügen"}
          </Button>
        </div>
      </div>

      <Dialog open={locationsOpen} onOpenChange={setLocationsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Teilnehmende Standorte · {card.name}
            </DialogTitle>
          </DialogHeader>

          {locations.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine Standorte hinterlegt.</div>
          ) : (
            <>
              <div className="space-y-3 mt-4">
                {locations.map((location) => (
                  <div key={location.id} className="border rounded-lg p-4 hover:bg-muted transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg mb-2">{location.name}</h4>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>{location.addressLine1}</p>
                          {location.addressLine2 ? <p>{location.addressLine2}</p> : null}
                          <p className="font-medium">
                            {location.postalCode} {location.city}
                          </p>
                        </div>
                      </div>
                      {location.latitude && location.longitude ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                            const url = isIOS
                              ? `https://maps.apple.com/?daddr=${location.latitude},${location.longitude}`
                              : `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`;
                            window.open(url, "_blank");
                          }}
                          className="flex-shrink-0"
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Route
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {(() => {
                const locationsWithCoords = locations.filter((location) => location.latitude && location.longitude);
                if (locationsWithCoords.length === 0) return null;

                const avgLat =
                  locationsWithCoords.reduce((sum, location) => sum + parseFloat(location.latitude), 0) /
                  locationsWithCoords.length;
                const avgLon =
                  locationsWithCoords.reduce((sum, location) => sum + parseFloat(location.longitude), 0) /
                  locationsWithCoords.length;

                return (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Kartenansicht
                    </h3>
                    <div className="h-80 rounded-lg overflow-hidden border">
                      <MapContainer
                        center={[avgLat, avgLon]}
                        zoom={locationsWithCoords.length === 1 ? 13 : 10}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {locationsWithCoords.map((location) => (
                          <Marker
                            key={location.id}
                            position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
                          >
                            <Popup>
                              <div className="text-sm">
                                <strong className="text-base">{location.name}</strong>
                                <br />
                                {location.addressLine1}
                                {location.addressLine2 ? (
                                  <>
                                    <br />
                                    {location.addressLine2}
                                  </>
                                ) : null}
                                <br />
                                {location.postalCode} {location.city}
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StampCardCard;
